import type { ContentBlock } from '@agentclientprotocol/sdk/experimental/v2'
import { useBridge } from '@web/adapter/useBridgeRuntime'
import { api, artifactFileUrl } from '@web/api/client'
import type { Command, QueuedMessage } from '@web/api/types'
import {
  MAX_MESSAGE_ATTACHMENT_BYTES,
  MAX_UPLOAD_BYTES,
  uploadSessionFile,
} from '@web/api/uploads'
import { Row, RowGroup, Sheet } from '@web/components/mobile/Sheet'
import { CircleButton } from '@web/components/mobile/TopBar'
import { SendButton } from '@web/components/SendButton'
import { Icon } from '@web/components/ui/icons'
import { appContextChips } from '@web/lib/app-context-chips'
import { currentAppContext, waitForAppContext } from '@web/lib/app-context-sync'
import { useNarrow } from '@web/lib/useNarrow'
import {
  composerSendMode,
  handleComposerEnter,
} from '@web/state/enter-key-behavior'
import { useIdentity } from '@web/state/identity'
import { useEnterKeyBehavior } from '@web/state/local-enter-key-behavior'
import {
  type ComposerAttachment as Attachment,
  getSessionStore,
} from '@web/state/session-store'
import { beginResume, markInteraction } from '@web/state/startup-telemetry'
import { describeWork } from '@web/state/working'
import {
  type ClipboardEvent,
  type DragEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

/**
 * send  — the session is idle: goes out immediately.
 * queue — a turn is open: held by the control plane until the turn ends.
 * steer — deliver into the active turn; the server falls back to interrupt-and-send.
 */
type SendMode = 'send' | 'queue' | 'steer'

/**
 * The composer talks to the bridge socket directly rather than through
 * assistant-ui's composer runtime: Claude Code sessions accept input while a
 * turn is running, and what happens to it (queue or steer) is the user's call.
 * Files can be pasted, dropped, or attached.
 */
export function Composer({ bindingId }: { bindingId?: string | null }) {
  const {
    snapshot,
    send,
    interrupt,
    queueStartupMessage,
    removeStartupMessage,
    sessionId,
  } = useBridge()
  const store = useMemo(() => getSessionStore(sessionId), [sessionId])
  const { acp, bridge, socket } = snapshot
  const [text, setText] = useState(() => store.getComposerDraft().text)
  const [attachments, setAttachments] = useState<Attachment[]>(
    () => store.getComposerDraft().attachments,
  )
  const [appContext, setAppContext] = useState<{
    sessionId: string
    bindingId: string
    value: unknown
  } | null>(null)
  const [factoryTask, setFactoryTask] = useState(() => store.getFactoryTask())
  const [showOptions, setShowOptions] = useState(false)
  const narrow = useNarrow()
  const [uploading, setUploading] = useState(0)
  const uploadInProgress = useRef(false)
  const [dragging, setDragging] = useState(false)
  const [attachError, setAttachError] = useState<string | null>(null)
  const [syncingContext, setSyncingContext] = useState(false)
  const submitting = useRef(false)
  const ref = useRef<HTMLTextAreaElement>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const supportsImages = !!acp.capabilities.session?.prompt?.image
  const supportsResources = !!acp.capabilities.session?.prompt?.embeddedContext
  const supportsAttachments =
    !!acp.capabilities.session?.prompt?.image ||
    !!acp.capabilities.session?.prompt?.embeddedContext
  const messageLabel = 'Message agent'
  useEffect(() => {
    store.saveComposerDraft({ text, attachments })
  }, [store, text, attachments])

  useEffect(() => {
    const select = (event: Event) => {
      if (
        (event as CustomEvent<{ sessionId: string }>).detail?.sessionId !==
        sessionId
      )
        return
      if (submitting.current) {
        if ((store.getFactoryTask()?.id ?? '') !== (factoryTask?.id ?? ''))
          store.selectFactoryTask(factoryTask)
        return
      }
      const draft = store.getComposerDraft()
      setFactoryTask(store.getFactoryTask())
      setText(draft.text)
      setAttachments(draft.attachments)
      requestAnimationFrame(() => ref.current?.focus())
    }
    window.addEventListener('bridge:factory-draft', select)
    window.addEventListener('bridge:focus-composer', select)
    return () => {
      window.removeEventListener('bridge:factory-draft', select)
      window.removeEventListener('bridge:focus-composer', select)
    }
  }, [store, sessionId, factoryTask])

  useEffect(() => {
    setAppContext(
      bindingId
        ? {
            sessionId,
            bindingId,
            value: currentAppContext(sessionId, bindingId),
          }
        : null,
    )
    if (!bindingId) return
    const update = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          sessionId?: string
          bindingId?: string
          context?: unknown
        }>
      ).detail
      if (detail?.sessionId !== sessionId || detail.bindingId !== bindingId)
        return
      setAppContext({ sessionId, bindingId, value: detail.context ?? null })
    }
    window.addEventListener('bridge:app-context-changed', update)
    return () =>
      window.removeEventListener('bridge:app-context-changed', update)
  }, [sessionId, bindingId])

  const { chips: contextChips, hiddenCount: hiddenContextCount } = useMemo(
    () =>
      appContextChips(
        appContext?.sessionId === sessionId &&
          appContext.bindingId === bindingId
          ? appContext.value
          : null,
      ),
    [appContext, sessionId, bindingId],
  )

  const work = describeWork(acp, bridge)
  const status = bridge.status
  const starting = status === 'starting' || status === 'created'
  // A sleeping or stopped session wakes on the first keystroke, and
  // the message waits in the startup outbox until the runner is ready.
  const asleep = ['asleep', 'closed', 'exited'].includes(status)
  const waking = starting || asleep
  const wakeRequested = useRef(false)
  useEffect(() => {
    if (!asleep) wakeRequested.current = false
  }, [asleep])
  const wake = useCallback(() => {
    if (!asleep || wakeRequested.current) return
    wakeRequested.current = true
    beginResume(sessionId, 'composer')
    api.resumeSession(sessionId).catch((failure: Error) => {
      wakeRequested.current = false
      setAttachError(failure.message)
    })
  }, [asleep, sessionId])
  const blocked =
    uploading > 0 ||
    bridge.record?.migration_status === 'diagnostic' ||
    (!waking &&
      (snapshot.hydrating ||
        !['ready', 'running', 'requires_action', 'idle'].includes(
          bridge.status,
        ) ||
        socket !== 'open'))
  const draftDisabled =
    uploading > 0 ||
    bridge.record?.migration_status === 'diagnostic' ||
    (!waking &&
      (!['ready', 'running', 'requires_action', 'idle'].includes(
        bridge.status,
      ) ||
        socket !== 'open'))
  // The composer is usable once the session is ready and connected; the mark
  // is recorded once per start this tab initiated and ignored otherwise.
  useEffect(() => {
    if (!blocked && !waking) markInteraction(sessionId, 'composer_ready')
  }, [blocked, waking, sessionId])
  // A turn is open: input queues by default and can steer instead.
  const turnOpen = work.working && work.tone !== 'off'
  const identity = useIdentity()
  const behavior = useEnterKeyBehavior(
    identity.state?.current_user?.enter_key_behavior,
  )
  const primaryMode = composerSendMode(behavior, 'primary', turnOpen, waking)
  const hasInput = !!text.trim() || attachments.length > 0

  // Grow with the text. An empty box keeps its natural single row: measuring
  // it would include the wrapped placeholder, and in a box that has not been
  // laid out yet that wraps into many lines and locks the max height in.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (!text) {
      el.style.height = ''
      return
    }
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`
  }, [text])

  const addFiles = useCallback(
    async (files: Iterable<File>) => {
      if (submitting.current) return
      if (uploadInProgress.current) {
        setAttachError(
          'Wait for the current upload to finish before attaching more files.',
        )
        return
      }
      if (!supportsAttachments) {
        setAttachError(
          'This agent accepts text messages. Paste the text into your message.',
        )
        return
      }
      const draftTaskId = store.getFactoryTask()?.id ?? ''
      let attachedCount = attachments.length
      let attachedBytes = attachments.reduce(
        (total, attachment) =>
          total +
          (attachment.upload?.files[0]?.size_bytes ??
            (attachment.kind === 'text'
              ? new TextEncoder().encode(attachment.text).length
              : Math.ceil((attachment.data.length * 3) / 4))),
        0,
      )
      setAttachError(null)
      uploadInProgress.current = true
      setUploading((count) => count + 1)
      try {
        for (const file of files) {
          try {
            if (file.type.startsWith('image/') && !supportsImages)
              throw new Error('This agent does not accept images.')
            if (!file.type.startsWith('image/') && !supportsResources)
              throw new Error('This agent does not accept embedded files.')
            if (file.size > MAX_UPLOAD_BYTES)
              throw new Error(`${file.name} is larger than 4 MB`)
            if (
              attachedCount >= 16 ||
              attachedBytes + file.size > MAX_MESSAGE_ATTACHMENT_BYTES
            )
              throw new Error(
                'A message can include up to 16 files and 8 MB of attachments.',
              )
            const { upload, data } = await uploadSessionFile(sessionId, file)
            const common = { id: upload.id, name: file.name, upload }
            const attachment: Attachment = file.type.startsWith('image/')
              ? { ...common, kind: 'image', mediaType: file.type, data }
              : looksLikeText(file)
                ? { ...common, kind: 'text', text: await file.text() }
                : {
                    ...common,
                    kind: 'file',
                    mediaType: file.type || 'application/octet-stream',
                    data,
                  }
            attachedCount++
            attachedBytes += file.size
            store.appendComposerAttachment(attachment, draftTaskId)
            if ((store.getFactoryTask()?.id ?? '') === draftTaskId)
              setAttachments(store.getComposerDraft().attachments)
          } catch (failure) {
            setAttachError((failure as Error).message)
          }
        }
      } finally {
        uploadInProgress.current = false
        setUploading((count) => count - 1)
      }
    },
    [
      attachments,
      store,
      sessionId,
      supportsAttachments,
      supportsImages,
      supportsResources,
    ],
  )

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (submitting.current) {
      e.preventDefault()
      return
    }
    const files = [...e.clipboardData.files]
    if (files.length) {
      e.preventDefault()
      void addFiles(files)
    }
  }

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(false)
    if (submitting.current) return
    if (e.dataTransfer.files.length) void addFiles([...e.dataTransfer.files])
  }

  const submit = useCallback(
    (mode: SendMode) => {
      const raw = text.trim()
      if ((!raw && !attachments.length) || blocked || submitting.current) return
      const t = factoryTask
        ? `[Factory task ${factoryTask.id}: ${factoryTask.title}]\n${raw}`
        : raw
      setAttachError(null)
      // "send" and "queue" are the same command: the control plane sends at
      // once when idle and holds otherwise. "steer" requests immediate delivery.
      let cmd: Command
      if (attachments.length) {
        if (
          attachments.some((attachment) =>
            attachment.kind === 'image' ? !supportsImages : !supportsResources,
          )
        ) {
          setAttachError(
            'The agent no longer supports one of the attached files. Remove it to continue.',
          )
          return
        }
        const content: ContentBlock[] = [
          ...(t ? [{ type: 'text' as const, text: t }] : []),
          ...attachments.map((attachment): ContentBlock => {
            if (attachment.kind === 'image')
              return {
                type: 'image',
                mimeType: attachment.mediaType,
                data: attachment.data,
              }
            const uploadedFile = attachment.upload?.files[0]
            const uri =
              attachment.upload && uploadedFile
                ? new URL(
                    artifactFileUrl(
                      sessionId,
                      attachment.upload.id,
                      uploadedFile.id,
                      false,
                      'uploads',
                    ),
                    window.location.origin,
                  ).href
                : `attachment:///${encodeURIComponent(attachment.name)}`
            return attachment.kind === 'text'
              ? {
                  type: 'resource',
                  resource: {
                    uri,
                    mimeType: 'text/plain',
                    text: attachment.text,
                  },
                }
              : {
                  type: 'resource',
                  resource: {
                    uri,
                    mimeType: attachment.mediaType,
                    blob: attachment.data,
                  },
                }
          }),
        ]
        cmd =
          mode === 'steer'
            ? { type: 'steer', content }
            : { type: 'queue', content }
      } else {
        cmd =
          mode === 'steer'
            ? { type: 'steer', text: t }
            : { type: 'queue', text: t }
      }
      cmd.send_mode =
        mode === 'send' ? 'normal' : mode === 'queue' ? 'queued' : 'steer'
      submitting.current = true
      void (async () => {
        try {
          if (bindingId) {
            setSyncingContext(true)
            await waitForAppContext(sessionId, bindingId)
          }
          if (asleep) wake()
          const accepted = waking
            ? queueStartupMessage(
                cmd.type === 'queue'
                  ? cmd
                  : 'content' in cmd
                    ? {
                        type: 'queue',
                        content: cmd.content,
                        send_mode: cmd.send_mode,
                      }
                    : {
                        type: 'queue',
                        text: cmd.text,
                        send_mode: cmd.send_mode,
                      },
              )
            : send(cmd)
          if (accepted) {
            markInteraction(sessionId, 'first_prompt_sent')
            setText((current) => (current === text ? '' : current))
            setAttachments((current) =>
              current === attachments ? [] : current,
            )
            ref.current?.focus()
          } else
            setAttachError(
              'The connection changed. Your message is kept here; try again when the session reconnects.',
            )
        } catch {
          setAttachError(
            'The selected app context could not be saved. Your message is kept here; try again.',
          )
        } finally {
          submitting.current = false
          setSyncingContext(false)
        }
      })()
    },
    [
      text,
      factoryTask,
      attachments,
      blocked,
      waking,
      asleep,
      wake,
      send,
      queueStartupMessage,
      supportsImages,
      supportsResources,
      bindingId,
      sessionId,
    ],
  )

  return (
    <div
      className={`composer flex flex-col gap-2 ${narrow ? 'm-composer' : ''}`}
    >
      {factoryTask && (
        <div className="factory-chat-context">
          <span>
            To coordinator · <strong>{factoryTask.title}</strong>
          </span>
          <button
            type="button"
            className="btn btn-sm"
            disabled={syncingContext}
            onClick={() => {
              if (submitting.current) return
              store.saveComposerDraft({ text, attachments })
              store.selectFactoryTask(null)
              window.dispatchEvent(
                new CustomEvent('bridge:factory-draft', {
                  detail: { sessionId },
                }),
              )
            }}
          >
            Clear task
          </button>
        </div>
      )}
      {(bridge.queue.some((item) => !store.isStartupMessage(item.id)) ||
        store.getPendingMessages().length > 0) && (
        <QueueStrip
          items={[
            ...bridge.queue.filter((item) => !store.isStartupMessage(item.id)),
            ...store
              .getPendingMessages()
              .filter(
                (item) => !bridge.queue.some((queued) => queued.id === item.id),
              ),
          ]}
          send={send}
          canSendNow={!blocked && !waking}
          onRemoveStartup={removeStartupMessage}
        />
      )}
      <div
        className={`card relative flex flex-col ${dragging ? 'border-accent' : ''}`}
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        {uploading > 0 && (
          <p role="status" className="px-3 pt-3 text-xs text-muted">
            Uploading files…
          </p>
        )}
        {(attachments.length > 0 || contextChips.length > 0) && (
          <div className="flex flex-wrap gap-2 px-3 pt-3">
            {attachments.map((a) => (
              <div
                key={a.id}
                className="relative flex items-center gap-1.5 rounded-control border border-line bg-inset p-1 pr-6 text-[11px]"
              >
                {a.kind === 'image' ? (
                  <img
                    src={`data:${a.mediaType};base64,${a.data}`}
                    alt={a.name}
                    className="h-10 w-10 rounded-sm object-cover"
                  />
                ) : (
                  <Icon name="fileText" size={14} className="ml-1 text-muted" />
                )}
                <span className="max-w-32 truncate">{a.name}</span>
                <button
                  className="absolute right-1 top-1 text-faint hover:text-fg"
                  disabled={syncingContext}
                  onClick={() => {
                    if (submitting.current) return
                    setAttachments((prev) => prev.filter((x) => x.id !== a.id))
                  }}
                  aria-label="Remove attachment"
                >
                  <Icon name="close" size={12} />
                </button>
              </div>
            ))}
            {contextChips.map((chip) => (
              <span
                key={chip.key}
                className="composer-context-chip"
                title={`Selected in app · ${chip.title}`}
              >
                <Icon name="attach" size={12} />
                <span>{chip.label}</span>
              </span>
            ))}
            {hiddenContextCount > 0 && (
              <span className="composer-context-chip">
                +{hiddenContextCount} more
              </span>
            )}
          </div>
        )}
        {attachError && (
          <div role="alert" className="px-4 pt-2 text-[12px] text-danger">
            {attachError}
          </div>
        )}
        <textarea
          ref={ref}
          rows={1}
          value={text}
          disabled={draftDisabled || syncingContext}
          aria-label={messageLabel}
          placeholder={
            blocked
              ? snapshot.hydrating || status === 'unknown'
                ? 'Connecting to session…'
                : socket !== 'open'
                  ? 'Reconnecting…'
                  : 'Resume to continue…'
              : asleep
                ? 'Message to wake the session…'
                : starting
                  ? 'Message while the session starts…'
                  : turnOpen
                    ? primaryMode === 'steer'
                      ? 'Steer the conversation…'
                      : 'Queue a message…'
                    : `${messageLabel}…`
          }
          onChange={(e) => {
            if (submitting.current) return
            setText(e.target.value)
            if (e.target.value.trim()) wake()
          }}
          onPaste={onPaste}
          onKeyDown={(e) => {
            if (submitting.current) {
              e.preventDefault()
              return
            }
            handleComposerEnter(e, behavior, setText, (action) =>
              submit(composerSendMode(behavior, action, turnOpen, waking)),
            )
            if (e.key === 'Escape') {
              if (attachError) setAttachError(null)
              else if (turnOpen && !text) interrupt()
            }
          }}
          className="max-h-[220px] w-full resize-none bg-transparent px-4 pb-2 pt-3 text-[14px] outline-none placeholder:text-faint disabled:opacity-60"
        />
        <div className="composer-actions flex items-center gap-1.5 px-2 pb-1.5">
          {narrow ? (
            <CircleButton
              icon="plus"
              label="Options"
              expanded={showOptions}
              onClick={() => setShowOptions(true)}
            />
          ) : (
            <>
              {supportsAttachments && (
                <button
                  className="btn btn-icon"
                  title="Attach files"
                  disabled={blocked || syncingContext}
                  onClick={() => fileInput.current?.click()}
                >
                  <Icon name="plus" size={14} />
                </button>
              )}
            </>
          )}
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files) void addFiles([...e.target.files])
              e.target.value = ''
            }}
          />
          <span className="flex-1" />
          {narrow && turnOpen && (
            <CircleButton
              icon={bridge.stopping ? 'loader' : 'stop'}
              tone="danger"
              label={
                bridge.stopping ? 'Stopping current turn' : 'Stop current turn'
              }
              disabled={blocked || bridge.stopping}
              onClick={interrupt}
            />
          )}

          <SendButton
            className={`btn btn-primary h-8 w-8 justify-center rounded-full px-0 ${narrow ? 'm-send' : ''}`}
            disabled={blocked || !hasInput || syncingContext}
            label={`${primaryMode === 'steer' ? 'Steer' : primaryMode === 'queue' ? 'Queue' : 'Send'}; long-press or right-click for Enter Key Behavior`}
            onSend={() => submit(primaryMode)}
          >
            <Icon name="send" size={16} />
          </SendButton>
        </div>
      </div>
      {narrow && (
        <>
          <Sheet
            open={showOptions}
            onClose={() => setShowOptions(false)}
            title="Options"
          >
            <RowGroup>
              {supportsAttachments && (
                <Row
                  icon="plus"
                  label="Attach files"
                  description="Files up to 4 MB, saved in this session."
                  disabled={blocked || syncingContext}
                  onClick={() => {
                    setShowOptions(false)
                    fileInput.current?.click()
                  }}
                />
              )}
              <Row
                icon="tool"
                label="Session settings"
                description="Model, permissions, scenario, context"
                chevron
                onClick={() => {
                  setShowOptions(false)
                  window.dispatchEvent(
                    new CustomEvent('bridge:session-settings', {
                      detail: { sessionId },
                    }),
                  )
                }}
              />
            </RowGroup>
          </Sheet>
        </>
      )}
    </div>
  )
}

/** Messages the control plane is holding. Each can be sent now (interrupting) or dropped. */
function QueueStrip({
  items,
  send,
  canSendNow,
  onRemoveStartup,
}: {
  items: QueuedMessage[]
  send: (cmd: Command) => boolean
  canSendNow: boolean
  onRemoveStartup: (id: string) => void
}) {
  const [open, setOpen] = useState<Record<string, boolean>>({})
  return (
    <div className="card overflow-hidden">
      <div className="icon-anim flex items-center gap-2 px-3 py-1.5 text-[11px] text-faint">
        <Icon name="clock" size={11} />
        <span className="label">Queued · {items.length}</span>
      </div>
      {items.map((q, i) => {
        const displayed = q.display_content ?? q.content
        const preview =
          q.display_content == null
            ? q.text
            : q.display_content
                .flatMap((part) => {
                  if (
                    part &&
                    typeof part === 'object' &&
                    'type' in part &&
                    part.type === 'text' &&
                    'text' in part &&
                    typeof part.text === 'string'
                  )
                    return [part.text]
                  return []
                })
                .join('\n')
        const hasAttachments = displayed?.some(
          (part) =>
            part &&
            typeof part === 'object' &&
            'type' in part &&
            part.type !== 'text',
        )
        const lines = preview.split('\n')
        const expanded = !!open[q.id]
        return (
          <div
            key={q.id}
            className="flex items-start gap-2 border-t border-line px-2 py-1.5"
          >
            <button
              className="mt-0.5 shrink-0 text-faint hover:text-fg"
              onClick={() =>
                setOpen((prev) => ({ ...prev, [q.id]: !expanded }))
              }
              aria-label={expanded ? 'Collapse' : 'Expand'}
            >
              {expanded ? (
                <Icon name="chevronDown" size={13} />
              ) : (
                <Icon name="chevronRight" size={13} />
              )}
            </button>
            <button
              className={`min-w-0 flex-1 text-left text-[13px] ${expanded ? 'whitespace-pre-wrap break-words' : 'truncate'}`}
              onClick={() =>
                setOpen((prev) => ({ ...prev, [q.id]: !expanded }))
              }
              title={expanded ? undefined : preview}
            >
              <span className="mono mr-2 text-[10px] text-faint">#{i + 1}</span>
              {expanded ? preview : lines[0]}
              {!expanded && (lines.length > 1 || hasAttachments) && (
                <span className="ml-2 text-[11px] text-faint">
                  {lines.length > 1 ? `+${lines.length - 1} lines` : ''}
                  {hasAttachments ? ' · attachments' : ''}
                </span>
              )}
            </button>
            {canSendNow && (
              <button
                className="btn btn-sm shrink-0"
                title="Send this message into the current turn now"
                onClick={() => send({ type: 'queue_send_now', id: q.id })}
              >
                <Icon name="zap" size={11} /> Steer
              </button>
            )}
            <button
              className="btn btn-icon shrink-0"
              title="Remove from the queue"
              onClick={() => {
                onRemoveStartup(q.id)
                send({ type: 'queue_remove', id: q.id })
              }}
            >
              <Icon name="close" size={13} />
            </button>
          </div>
        )
      })}
    </div>
  )
}

const looksLikeText = (f: File) =>
  f.type.startsWith('text/') ||
  f.type === 'application/json' ||
  /\.(md|txt|json|csv|log|ya?ml|toml|py|ts|tsx|js|jsx|sh|css|html|xml|sql|rs|go|java|rb)$/i.test(
    f.name,
  )
